import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  TextLink,
  TextLinkStyle,
  SelectorType,
  Tab,
  TabDesign,
  TabLayout,
  Theme,
  useTranslation,
  FAIcon,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import { SearchTrackingTypes, updateSearchTrackingType } from '@commerce-search-webcomponents/sdk';
import { popularSearchKeywordKeys } from '../types/constants';
import { SuggestionsProps } from './types/PropTypes';
import styles from './Suggestions.module.scss';

const Suggestions = ({ autoCompleteResults, searchText, isShowNoResult, onSuggestionClick }: SuggestionsProps) => {
  const savedRecentSearches = (StorageService.getLocalStorage()?.retrieve('recentSearches') || []) as string[];
  const [recentSearches, setRecentSearches] = useState<string[]>(savedRecentSearches);
  const t = useTranslation();
  const dispatch = useDispatch();
  const recentSearchesExist = savedRecentSearches.length !== 0;
  const enableNoResultMessage = isShowNoResult && autoCompleteResults.length === 1;

  const popularSearchKeywords = useMemo(
    () => popularSearchKeywordKeys.map((translationKey: string) => t(translationKey)),
    [popularSearchKeywordKeys],
  );
  useEffect(() => {
    if (!recentSearchesExist) {
      setRecentSearches(popularSearchKeywords);
    }
  }, [recentSearches]);

  const magnifyIcon = (
    <FAIcon iconName="magnifying-glass" className={styles.icon} theme={Theme.GRAY} data-testid="search-icon-test-id" />
  );

  const suggestionLists = useMemo(
    (): Array<JSX.Element> =>
      autoCompleteResults.map(({ title }) => {
        let str = '';
        let lastIndex = 0;
        const text = searchText.trim().replace(/[^\w\s]/gi, ''); // Remove special chars to prevent regex error
        Array.from(title.matchAll(new RegExp(text, 'ig'))).forEach((match) => {
          str += title.slice(lastIndex, match.index);
          lastIndex = (match.index as number) + match[0].length;
          str += `<b>${title.slice(match.index, lastIndex)}</b>`;
        });
        if (lastIndex !== title.length) {
          str += title.slice(lastIndex, title.length);
        }

        return (
          <li
            key={title}
            className={`${styles['suggestion-text']} ${enableNoResultMessage && styles['max-width-limit']}`}
            role="presentation"
            onClick={(e) => handleSuggestionClick(e, title, SearchTrackingTypes.AUTOCOMPLETE)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSuggestionClick(e, title, SearchTrackingTypes.AUTOCOMPLETE);
              }
            }}
          >
            {magnifyIcon}
            <p dangerouslySetInnerHTML={{ __html: str }} />
            {enableNoResultMessage && (
              <div className={styles['no-result-message']}>{t('search.suggestions.noResultsMessage')}</div>
            )}
          </li>
        );
      }),
    [searchText, autoCompleteResults],
  );

  const recentSearchesLists = useMemo(
    (): Array<JSX.Element> =>
      recentSearches?.map((search: string) => (
        <li
          key={search}
          className={styles['suggestion-text']}
          role="presentation"
          onClick={() =>
            handleSuggestionClick(
              undefined,
              search,
              recentSearchesExist ? SearchTrackingTypes.RECENT : SearchTrackingTypes.SUGGESTED,
            )
          }
        >
          {magnifyIcon}
          <p>
            <b>{search}</b>
          </p>
        </li>
      )),
    [recentSearches],
  );

  const savedRecentSearchTabLists = useMemo(
    (): Array<JSX.Element> =>
      savedRecentSearches?.map((search: string) => (
        <Tab
          title={search}
          key={search}
          selectorType={SelectorType.CIRCLE}
          layout={TabLayout.HORIZONTAL}
          design={TabDesign.PILL}
          className={styles['saved-recent-tab-list']}
          onTabClick={() => handleSuggestionClick(undefined, search, SearchTrackingTypes.RECENT)}
        />
      )),
    [savedRecentSearches],
  );

  const handleSuggestionClick = (
    e: React.MouseEvent | React.KeyboardEvent | undefined,
    text: string,
    searchType: SearchTrackingTypes,
  ) => {
    dispatch(updateSearchTrackingType(searchType));
    onSuggestionClick(e, text);
  };

  const handleClearAllClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setRecentSearches([]);
    StorageService.getLocalStorage()?.remove('recentSearches');
  };

  return (
    <>
      {autoCompleteResults.length === 0 ? (
        <div className={styles['recent-searches']}>
          <div className={styles['recent-searches-heading']}>
            <span className={styles['recent-searches-text']}>
              {recentSearchesExist ? t('search.suggestions.recentSearches') : t('search.suggestions.peopleSearchedFor')}
            </span>
            {recentSearchesExist && (
              <span className={styles['recent-searches-clear']}>
                <TextLink
                  data-testid="clear-all-test-id"
                  text={t('search.common.clearAll')}
                  textLinkStyle={TextLinkStyle.UNDERLINE}
                  theme={Theme.PURPLE}
                  onClick={handleClearAllClick}
                />
              </span>
            )}
          </div>
          {!recentSearchesExist && <ul className={styles['suggestion-lists']}>{recentSearchesLists}</ul>}
          {recentSearchesExist && <ul className={styles['suggestion-tab-lists']}>{savedRecentSearchTabLists}</ul>}
        </div>
      ) : (
        <div className={styles.suggestions} data-testid="suggestion-test-id">
          <ul className={styles['suggestion-lists']}>{suggestionLists}</ul>
        </div>
      )}
    </>
  );
};

export default Suggestions;
